import type { FC, RefObject } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  alpha,
  Box,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@/shared/ui/mui';
import { featureMenuSections, getFeaturePath } from '@/features/landing/unified/data/featureCatalog';

interface FeaturesMegaMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  anchorWidth: number | undefined;
  isDarkNav: boolean;
  onClose: (event: Event | React.SyntheticEvent) => void;
  onItemClick: () => void;
  featuresAnchorRef: RefObject<HTMLButtonElement>;
}

export const FeaturesMegaMenu: FC<FeaturesMegaMenuProps> = ({
  open,
  anchorEl,
  anchorWidth,
  isDarkNav,
  onClose,
  onItemClick,
  featuresAnchorRef,
}) => {
  const theme = useTheme();

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      featuresAnchorRef.current &&
      featuresAnchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    onClose(event);
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      disablePortal
      style={{ zIndex: 1300 }}
    >
      {({ TransitionProps }) => (
        <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
          <Paper
            elevation={10}
            sx={{
              mt: 1.25,
              borderRadius: 2.5,
              border: '1px solid',
              borderColor: isDarkNav
                ? alpha(theme.palette.common.white, 0.1)
                : theme.palette.divider,
              backgroundColor: isDarkNav
                ? theme.palette.black.main
                : theme.palette.common.white,
              width: anchorWidth ?? '100%',
              overflow: 'hidden',
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 0,
                  p: 3,
                }}
              >
                {featureMenuSections.map((section, sectionIdx) => (
                  <Box
                    key={section.id}
                    sx={{
                      px: 2,
                      borderLeft: sectionIdx > 0 ? '1px solid' : 'none',
                      borderColor: isDarkNav
                        ? alpha(theme.palette.common.white, 0.12)
                        : 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        color: isDarkNav
                          ? alpha(theme.palette.primary.light, 0.9)
                          : theme.palette.primary.main,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        mb: 1.5,
                      }}
                    >
                      {section.title}
                    </Typography>
                    {section.features.map((item) => (
                      <Box
                        key={item.slug}
                        component={RouterLink}
                        to={getFeaturePath(item.slug)}
                        onClick={onItemClick}
                        sx={{
                          display: 'block',
                          color: isDarkNav
                            ? alpha(theme.palette.common.white, 0.8)
                            : theme.palette.text.primary,
                          textDecoration: 'none',
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          py: 0.9,
                          px: 1,
                          borderRadius: 1,
                          transition: 'background-color 0.15s ease, color 0.15s ease',
                          '&:hover': {
                            backgroundColor: isDarkNav
                              ? alpha(theme.palette.common.white, 0.1)
                              : alpha(theme.palette.primary.main, 0.08),
                            color: isDarkNav
                              ? theme.palette.common.white
                              : theme.palette.primary.main,
                          },
                        }}
                      >
                        {item.name}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};
